/* eslint-disable react/display-name */
import React from 'react';
import { Column, TableInstance, ColumnInstance } from 'react-table';

import Header from './tableHeader';
import FormsOptions from './tableFormsOptions';

import { getFormatDate } from 'utils/dateHelper';

import { FormList } from 'types/forms';

export default function useFormsColumns(
  columnsList?: Array<string>,
  skipOptions?: boolean,
): Array<Column<FormList>> {
  return React.useMemo((): Array<Column<FormList>> => {
    if (columnsList) {
      const columns = [];
      columnsList.forEach(columnName => {
        if (columnName === 'Id') columns.push(getIDHeader(Header));
        if (columnName === 'CID') columns.push(getCIDHeader(Header));
        if (columnName === 'Account')
          columns.push(getAccountNameHeader(Header));
        if (columnName === 'Last Updated By')
          columns.push(getLastUpdatedByHeader(Header));
        if (columnName === 'Date Last Updated')
          columns.push(getDateLastUpdatedHeader(Header));
        if (columnName === 'Form Status')
          columns.push(getFormStatusHeader(Header));
        if (columnName === 'Form Notes')
          columns.push(getFormNotesHeader(Header));
      });
      if (!skipOptions) columns.push(getOptionsHeader());
      return columns;
    }
    return [
      getIDHeader(Header),
      getCIDHeader(Header),
      getAccountNameHeader(Header),
      getLastUpdatedByHeader(Header),
      getDateLastUpdatedHeader(Header),
      getFormStatusHeader(Header),
      getFormNotesHeader(Header),
      getOptionsHeader(),
    ];
  }, [columnsList, skipOptions]);
}

function getIDHeader(
  Header: (
    props: TableInstance<FormList> & {
      column: ColumnInstance<FormList>;
    },
  ) => JSX.Element,
): Column<FormList> {
  return {
    Header: Header,
    accessor: (row: FormList) => `${row.Id || ''}`,
    id: 'Id',
  };
}

function getCIDHeader(
  Header: (
    props: TableInstance<FormList> & {
      column: ColumnInstance<FormList>;
    },
  ) => JSX.Element,
): Column<FormList> {
  return {
    Header: Header,
    accessor: (row: FormList) => `${row.Account?.CID || ''}`,
    id: 'CID',
  };
}

function getAccountNameHeader(
  Header: (
    props: TableInstance<FormList> & {
      column: ColumnInstance<FormList>;
    },
  ) => JSX.Element,
): Column<FormList> {
  return {
    Header: Header,
    accessor: (row: FormList) => `${row.Account?.Name || ''}`,
    id: 'Account Name',
  };
}

function getLastUpdatedByHeader(
  Header: (
    props: TableInstance<FormList> & {
      column: ColumnInstance<FormList>;
    },
  ) => JSX.Element,
): Column<FormList> {
  return {
    id: 'Last Updated By',
    Header: Header,
    accessor: 'LastModifiedBy',
  };
}

function getDateLastUpdatedHeader(
  Header: (
    props: TableInstance<FormList> & {
      column: ColumnInstance<FormList>;
    },
  ) => JSX.Element,
): Column<FormList> {
  return {
    id: 'Date Last Updated',
    Header: Header,
    accessor: row =>
      row.LastModified !== null ? getFormatDate(row.LastModified) : '',
  };
}

function getFormStatusHeader(
  Header: (
    props: TableInstance<FormList> & {
      column: ColumnInstance<FormList>;
    },
  ) => JSX.Element,
): Column<FormList> {
  return {
    Header: Header,
    accessor: (row: FormList) => `${row.SurveyStatus?.Description || ''}`,
    id: 'Form Status',
  };
}

function getFormNotesHeader(
  Header: (
    props: TableInstance<FormList> & {
      column: ColumnInstance<FormList>;
    },
  ) => JSX.Element,
): Column<FormList> {
  return {
    Header: Header,
    accessor: row => row.Notes,
    id: 'Form Notes',
  };
}

function getOptionsHeader(): Column<FormList> {
  return {
    id: 'Options',
    Header: 'Action',
    Cell: FormsOptions,
  };
}
